/**
 * Resolve marketplace / sales channel for failed (and similar) orders.
 * Uses stored fields first, then Excel originals, then product URLs / ASIN.
 */

import importerrLogo from "../website/assets/images/common/comimport.png";

const PLATFORM_META = {
  importerr: {
    key: "importerr",
    label: "Importerr",
    iconUrl: importerrLogo,
  },
  amazon: {
    key: "amazon",
    label: "Amazon",
    iconUrl: null,
  },
  flipkart: {
    key: "flipkart",
    label: "Flipkart",
    iconUrl: null,
  },
  myntra: {
    key: "myntra",
    label: "Myntra",
    iconUrl: null,
  },
  meesho: {
    key: "meesho",
    label: "Meesho",
    iconUrl: null,
  },
  snapdeal: {
    key: "snapdeal",
    label: "Snapdeal",
    iconUrl: null,
  },
  shopify: {
    key: "shopify",
    label: "Shopify",
    iconUrl: null,
  },
  manual: {
    key: "manual",
    label: "Manual",
    iconUrl: null,
  },
  unknown: {
    key: "unknown",
    label: "Other",
    iconUrl: null,
  },
};

function matchPlatformKey(text) {
  if (text == null || text === "") return null;
  const s = String(text).toLowerCase().trim();
  if (!s) return null;
  if (s.includes("importerr")) return "importerr";
  if (s.includes("amazon") || s === "amz" || s.includes("amazon.in")) return "amazon";
  if (s.includes("flipkart") || s.includes("flip-kart")) return "flipkart";
  if (s.includes("myntra")) return "myntra";
  if (s.includes("meesho")) return "meesho";
  if (s.includes("snapdeal")) return "snapdeal";
  if (s.includes("shopify")) return "shopify";
  if (s.includes("manual")) return "manual";
  return null;
}

/** Aligns with admin order list "Importerr" source tab: not Amazon / Shopify MKP on marketplace or panel. */
function isImporterrChannelOrder(order) {
  const m = String(order?.marketplace ?? "").trim();
  const p = String(order?.panel ?? "").trim();
  const blob = `${m} ${p}`;
  if (/shopify/i.test(blob)) return false;
  if (/amazon/i.test(blob)) return false;
  if (/flipkart/i.test(blob)) return false;
  if (/myntra/i.test(blob)) return false;
  if (/meesho/i.test(blob)) return false;
  if (/snapdeal/i.test(blob)) return false;
  return true;
}

function inferFromProductSignals(order) {
  const details = order?.orderDetails || [];
  const urls = details.map((d) => d?.productUrl).filter(Boolean).join(" ");
  if (/amazon\.|amzn\.|\/dp\/|\/gp\/product/i.test(urls)) return "amazon";
  if (/flipkart\.com/i.test(urls)) return "flipkart";
  if (/myntra\.com/i.test(urls)) return "myntra";
  if (/meesho\.com/i.test(urls)) return "meesho";
  if (/snapdeal\.com/i.test(urls)) return "snapdeal";
  if (/shopify\.com|myshopify\.com/i.test(urls)) return "shopify";

  const hasAsin = details.some((d) => {
    const a = d?.ASIN;
    return a && String(a).trim() && !/^\d+$/.test(String(a).trim());
  });
  if (hasAsin) return "amazon";

  return null;
}

export function getOrderPlatformMeta(order) {
  const excel = order?.originalExcelData || {};
  const candidates = [
    order?.marketplace,
    order?.panel,
    excel["MKP Name"],
    excel["Marketplace"],
    excel["marketplace"],
    excel["Panel"],
    excel["panel"],
  ];

  for (const c of candidates) {
    const key = matchPlatformKey(c);
    if (key) return { ...PLATFORM_META[key] };
  }

  const inferred = inferFromProductSignals(order);
  if (inferred) return { ...PLATFORM_META[inferred] };

  if (isImporterrChannelOrder(order)) {
    return { ...PLATFORM_META.importerr };
  }

  return { ...PLATFORM_META.unknown };
}
